import Pagination from "@/components/Pagination";
import { useEffect, useRef, useState } from "react";
import { Check, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { fmt } from "@/lib/format";
import { balanceStatus, STATUS_TEXT_CLASS, isHighBalance, daysSince, INACTIVE_DAYS_THRESHOLD } from "@/lib/thresholds";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import Drawer from "@/components/Drawer";
import UserInlineDetail from "@/components/user/UserInlineDetail";

export default function TopUsersTable({ users, sort, onSortChange, onAdjusted, selectMode, selectedEmails, onToggleEmail, onToggleAllVisible }) {
  // 페이지네이션(2026-07-20) — 수백 명 스크롤 불편 해소. 목록(참조)이 바뀌면 1페이지로.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => { setPage(1); }, [users]);
  const pageUsers = users.slice((Math.min(page, Math.max(1, Math.ceil(users.length / pageSize))) - 1) * pageSize,
                               (Math.min(page, Math.max(1, Math.ceil(users.length / pageSize))) - 1) * pageSize + pageSize);
  // 행 클릭 시 별도 페이지(/users/:userId)로 이동하던 걸 인라인 토글로 바꿨다가(2026-07-15),
  // 표가 옆으로 너무 넓어진다는 피드백으로 우측 슬라이드 패널(Drawer)로 다시 변경.
  // 크레딧 조정은 Drawer 폼 대신 행 안의 +/- 퀵 팝오버로 교체(2026-07-18 피드백 —
  // "+ 와 - 두고, 누르면 입력하고 체크하면 충전").
  const [detailUser, setDetailUser] = useState(null); // { userId, email } | null

  if (!users || users.length === 0) {
    return <div className="rounded-xl bg-ink-50 p-8 text-center text-sm text-ink-400">데이터 없음</div>;
  }

  // 메일 수신자 선택(2026-07-28) — "선택" 버튼으로 켜는 selectMode 에서만 체크박스 노출.
  // 이메일 없는 계정(anon 등)은 선택 대상에서 제외.
  // 헤더 체크박스는 "현재 화면(검색·필터 적용) 목록" 기준 — 검색해서 좁힌 뒤 전체선택하는 흐름.
  const selectableEmails = users.filter((u) => u.email).map((u) => u.email.toLowerCase());
  const allVisibleSelected =
    selectableEmails.length > 0 && selectableEmails.every((e) => selectedEmails?.has(e));
  // 표는 이제 상위 Panel 카드 안에 그대로 얹힘 — 자체 테두리·배경 없이 은은한 가로 구분선만
  // (2026-07-15 리디자인, "박스 안에 박스" 느낌 제거).
  return (
    <div className="-mx-5 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-ink-100">
            {selectMode && (
              <Th>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() => onToggleAllVisible?.(selectableEmails)}
                  title="현재 목록 전체 선택 (검색·필터 적용 기준)"
                  className="h-5 w-5 cursor-pointer accent-brand-600 align-middle"
                />
              </Th>
            )}
            <Th>#</Th>
            <SortableTh columnKey="email" sort={sort} onSortChange={onSortChange}>이메일 / User ID</SortableTh>
            <SortableTh columnKey="createdAt" sort={sort} onSortChange={onSortChange}>가입일시 (KST)</SortableTh>
            <SortableTh columnKey="balance" align="right" sort={sort} onSortChange={onSortChange} title="단위: 크레딧">현재 잔액</SortableTh>
            <SortableTh columnKey="ncUsed" align="right" sort={sort} onSortChange={onSortChange} title="단위: 크레딧">사용량 (선택 기간)</SortableTh>
            <SortableTh columnKey="chargeCount" align="right" sort={sort} onSortChange={onSortChange}>충전 횟수</SortableTh>
            <SortableTh columnKey="lastSeen" align="right" sort={sort} onSortChange={onSortChange}>최근 사용 (KST)</SortableTh>
            <Th align="right">조정</Th>
          </tr>
        </thead>
        <tbody>
          {pageUsers.map((u, i) => {
            const balClass = STATUS_TEXT_CLASS[balanceStatus(u.balance)];
            return (
              <tr
                key={u.userId}
                className="cursor-pointer border-b border-ink-50 hover:bg-ink-50/70"
                onClick={() => setDetailUser({ userId: u.userId, email: u.email })}
              >
                {/* 행 클릭(상세 드로어)과 충돌하지 않게 stopPropagation — QuickAdjust 와 같은 수법 */}
                {selectMode && (
                  <Td>
                    <input
                      type="checkbox"
                      checked={!!u.email && !!selectedEmails?.has(u.email.toLowerCase())}
                      disabled={!u.email}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => onToggleEmail?.(u.email.toLowerCase())}
                      title={u.email ? "메일 수신자로 선택" : "이메일 없는 계정은 선택 불가"}
                      className="h-5 w-5 cursor-pointer accent-brand-600 align-middle disabled:cursor-not-allowed"
                    />
                  </Td>
                )}
                <Td className="text-ink-400">{(Math.min(page, Math.max(1, Math.ceil(users.length / pageSize))) - 1) * pageSize + i + 1}</Td>
                <Td>
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-ink-900">{u.email || "(이메일 없음)"}</span>
                    <UserBadges balance={u.balance} lastSeen={u.lastSeen} />
                  </div>
                  <div className="font-mono text-xs text-ink-400">{u.userId.slice(0, 12)}…</div>
                </Td>
                {/* createdAtIso(UTC ISO, 시각 포함)가 있으면 KST 시분까지, 없으면(구 캐시·
                    sedaily 풀) 날짜만 — lastSeen 과 같은 포맷으로 통일(2026-07-21). */}
                <Td className="whitespace-nowrap text-ink-600">
                  {u.createdAtIso ? fmt.kstDateTime(u.createdAtIso).slice(0, 16) : (u.createdAt || "-")}
                </Td>
                <Td align="right" className={`text-base font-semibold tabular-nums ${balClass}`}>{fmt.nc(u.balance)}</Td>
                <Td align="right" className="tabular-nums text-ink-600">{fmt.nc(u.ncUsed)}</Td>
                <Td align="right" className="tabular-nums text-ink-600">{fmt.int(u.chargeCount)}</Td>
                <Td align="right" className="text-xs text-ink-500">{u.lastSeen ? fmt.kstDateTime(u.lastSeen).slice(0, 16) : "-"}</Td>
                <Td align="right">
                  <QuickAdjust user={u} onSuccess={onAdjusted} />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination total={users.length} page={page} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} />

      <Drawer open={!!detailUser} onClose={() => setDetailUser(null)} title={detailUser?.email || "사용자 상세"} wide>
        {detailUser && <UserInlineDetail userId={detailUser.userId} onAdjusted={onAdjusted} />}
      </Drawer>
    </div>
  );
}

// 행 안의 +/- 퀵 조정(2026-07-18) — Drawer 폼(3필드+이력)을 열 것 없이 그 자리에서
// 수량·사유 입력 후 체크로 즉시 지급/회수한다. 안전장치는 Drawer 폼과 동일 기준:
// 10만 크레딧 이상 또는 적용 후 잔액 마이너스면 체크를 한 번 더 눌러 확인해야 적용.
// 팝오버는 표의 overflow 컨테이너에 잘리지 않게 fixed 로 띄운다.
const LARGE_AMOUNT_THRESHOLD = 100_000;

function QuickAdjust({ user, onSuccess }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [sign, setSign] = useState(null); // 1(지급) | -1(회수) | null(닫힘)
  const [pos, setPos] = useState(null);   // { top | bottom, right }
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (sign == null) return;
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) close();
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [sign]);

  function open(e, s) {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    // 화면 아래쪽 행에서는 위로 펼친다(팝오버 높이 ~230px).
    const openUp = r.bottom + 240 > window.innerHeight;
    setPos({
      right: Math.max(8, window.innerWidth - r.right),
      ...(openUp ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
    });
    setSign(s);
    setAmount("");
    setReason("");
    setConfirming(false);
    setError(null);
  }

  function close() {
    setSign(null);
    setConfirming(false);
    setError(null);
  }

  const mag = Number(amount);
  const validMag = Number.isInteger(mag) && mag > 0;
  const validReason = reason.trim().length >= 3;
  const signed = (sign || 0) * mag;
  const expected = validMag ? user.balance + signed : null;
  const risky = validMag && (mag >= LARGE_AMOUNT_THRESHOLD || expected < 0);

  async function submit() {
    if (!validMag) { setError("1 이상의 정수를 입력하세요."); return; }
    if (!validReason) { setError("사유를 3자 이상 입력하세요."); return; }
    if (risky && !confirming) { setConfirming(true); setError(null); return; }
    setSubmitting(true);
    setError(null);
    try {
      await analyticsApi.adminAdjustCredit(user.userId, { amount: signed, reason: reason.trim() });
      onSuccess?.({ userIds: [user.userId], amount: signed });
      close();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <span className="relative inline-flex gap-1" ref={ref} onClick={(e) => e.stopPropagation()}>
      {readOnly ? (
        <span
          data-testid="quickadjust-readonly"
          title="스테이징(STG) 조회 상태에서는 크레딧 지급·회수가 비활성화됩니다"
          className="inline-flex items-center rounded-lg bg-ink-100 px-2 py-1 text-[11px] font-medium text-ink-400"
        >
          조회 전용
        </span>
      ) : (
      <>
      <button
        type="button"
        onClick={(e) => open(e, 1)}
        title="크레딧 지급"
        className={`flex h-7 w-7 items-center justify-center rounded-lg text-sm font-semibold ${
          sign === 1 ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        }`}
      >
        +
      </button>
      <button
        type="button"
        onClick={(e) => open(e, -1)}
        title="크레딧 회수"
        className={`flex h-7 w-7 items-center justify-center rounded-lg text-sm font-semibold ${
          sign === -1 ? "bg-red-600 text-white" : "bg-red-50 text-red-600 hover:bg-red-100"
        }`}
      >
        −
      </button>
      </>
      )}

      {sign != null && (
        <div
          style={pos}
          className="fixed z-50 w-60 max-w-[calc(100vw-1rem)] rounded-xl border border-ink-100 bg-white p-3 text-left shadow-[0_8px_24px_rgba(15,23,42,0.16)]"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className={`text-xs font-semibold ${sign === 1 ? "text-emerald-700" : "text-red-600"}`}>
              {sign === 1 ? "지급 (+)" : "회수 (−)"}
            </span>
            <button type="button" onClick={close} aria-label="닫기" className="rounded p-0.5 text-ink-400 hover:bg-ink-100">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="1"
              autoFocus
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setConfirming(false); }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="수량"
              className="w-full min-w-0 rounded-md border border-ink-200 px-2 py-1.5 text-sm tabular-nums focus:border-brand-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              title={confirming ? "한 번 더 눌러 확인" : "적용"}
              className={`flex h-8 w-9 shrink-0 items-center justify-center rounded-md text-white disabled:opacity-50 ${
                confirming ? "bg-amber-500 hover:bg-amber-600" : "bg-ink-900 hover:bg-ink-700"
              }`}
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => { setReason(e.target.value); setConfirming(false); }}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="사유 (필수)"
            className="mt-1.5 w-full rounded-md border border-ink-200 px-2 py-1.5 text-xs focus:border-brand-400 focus:outline-none"
          />
          {validMag && (
            <div className="mt-1.5 text-xs text-ink-500">
              적용 후 <b className={`tabular-nums ${expected < 0 ? "text-red-600" : "text-ink-900"}`}>{fmt.nc(expected)}</b>
            </div>
          )}
          {confirming && !error && (
            <div className="mt-1.5 text-xs text-amber-700">
              {expected < 0 ? "적용 후 잔액이 마이너스입니다." : "10만 크레딧 이상 큰 금액입니다."} 체크를 한 번 더 누르면 적용됩니다.
            </div>
          )}
          {error && <div className="mt-1.5 text-xs text-red-600">{error}</div>}
        </div>
      )}
    </span>
  );
}

// 이상·우선 조정 대상을 숫자 비교 없이 한눈에 찾도록(QA TBL-001) — 잔액부족/고액보유/
// 장기미사용 3종 배지. 조건 안 걸리면 아무것도 안 그려 목록이 배지로 뒤덮이지 않게 함.
function UserBadges({ balance, lastSeen }) {
  const status = balanceStatus(balance);
  const inactive = daysSince(lastSeen);
  return (
    <span className="flex items-center gap-1">
      {status !== "ok" && (
        <Badge tone={status === "bad" ? "bad" : "warn"}>잔액부족</Badge>
      )}
      {isHighBalance(balance) && <Badge tone="brand">고액보유</Badge>}
      {inactive != null && inactive >= INACTIVE_DAYS_THRESHOLD && (
        <Badge tone="default">미사용 {inactive}일</Badge>
      )}
    </span>
  );
}

function Badge({ tone, children }) {
  const cls = {
    bad:     "bg-red-50 text-red-700 border-red-200",
    warn:    "bg-amber-50 text-amber-700 border-amber-200",
    brand:   "bg-brand-50 text-brand-700 border-brand-200",
    default: "bg-ink-100 text-ink-500 border-ink-200",
  }[tone] || "bg-ink-100 text-ink-500 border-ink-200";
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none ${cls}`}>
      {children}
    </span>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`first:pl-5 last:pr-5 px-3 py-3 text-${align} text-xs font-semibold uppercase tracking-wide text-ink-500`}>
      {children}
    </th>
  );
}

// 칼럼 헤더를 눌러 오름/내림차순 토글 — 활성 칼럼에만 화살표(▲/▼)를 보여줘 지금 어떤 기준으로
// 정렬돼 있는지 한눈에 알 수 있게 함. 예전 "정렬" 프리셋 칩 5개를 대체(2026-07-15).
// 활성 칼럼이 아니어도 흐린 양방향 화살표를 항상 보여줘 "이 칼럼도 눌러서 정렬할 수 있다"는
// 걸 미리 알 수 있게 함(피드백: 화살표가 기본적으로 보였으면 좋겠다).
function SortableTh({ columnKey, children, align = "left", sort, onSortChange, title }) {
  const isActive = sort?.key === columnKey;
  const ActiveIcon = sort?.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th title={title} className={`first:pl-5 last:pr-5 px-3 py-3 text-${align} text-xs font-semibold uppercase tracking-wide text-ink-500`}>
      <button
        type="button"
        onClick={() => onSortChange(columnKey)}
        className={`inline-flex items-center gap-1 hover:text-ink-800 ${align === "right" ? "flex-row-reverse" : ""} ${isActive ? "text-ink-900" : ""}`}
      >
        {children}
        {isActive ? <ActiveIcon className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5 text-ink-300" />}
      </button>
    </th>
  );
}

function Td({ children, align = "left", className = "" }) {
  return <td className={`first:pl-5 last:pr-5 px-3 py-3 text-${align} ${className}`}>{children}</td>;
}
